import { useEffect, useState } from "react";
import API from "../../services/api";
import { Pagination, Modal, Input, Typography, Spin, Button, App, Empty } from "antd";
import { EyeOutlined, CheckCircleOutlined, CloseCircleOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";

import "./AdminCommercialApprovals.css";

const { Title, Text } = Typography;


function AdminCommercialApprovals() {
  const [spaces, setSpaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [total, setTotal] = useState(0);
  const navigate = useNavigate();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [selectedId, setSelectedId] = useState(null);

  const { message } = App.useApp();

  /* ── Helpers ──────────────────────────────────────────────── */
  const openRejectModal = (id) => { setSelectedId(id); setIsModalOpen(true); };

  const closeModal = () => { setIsModalOpen(false); setReason(""); };

  /* ── Fetch ────────────────────────────────────────────────── */
  const fetchPendingSpaces = async (page = 1) => {
    try {
      setLoading(true);
      const res = await API.get(`/commercial-spaces/pending/all?page=${page}&limit=15`);
      setSpaces(res.data.listings);
      setTotal(res.data.total);
      setCurrentPage(res.data.currentPage);
    } catch {
      message.error("Failed to load pending listings");
      setSpaces([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPendingSpaces(currentPage); }, [currentPage]);

  /* ── Actions ──────────────────────────────────────────────── */
  const handleApprove = async (id) => {
    try {
      await API.put(`/commercial-spaces/approve/${id}`);
      message.success("Listing approved successfully");
      fetchPendingSpaces(currentPage);
    } catch {
      message.error("Approval failed");
    }
  };

  const handleConfirmReject = async () => {
    try {
      await API.put(`/commercial-spaces/reject/${selectedId}`, { reason });
      message.success("Listing rejected with reason");
      closeModal();
      fetchPendingSpaces(currentPage);
    } catch (err) {
      message.error(err.response?.data?.message || "Reject failed");
    }
  };

  /* ── Loader ───────────────────────────────────────────────── */
  if (loading) {
    return (
      <div className="aca-loader">
        <Spin size="large" />
      </div>
    );
  }

  /* ── Render ───────────────────────────────────────────────── */
  return (
    <div className="aca-page">

      {/* HEADER */}
      <div className="aca-header">
        <div className="aca-header__left">
          <Title level={3} className="aca-header__title">Commercial Approvals</Title>
          <Text className="aca-header__subtitle">Review pending commercial space listings</Text>
        </div>
        <div className="aca-count-box">
          <span className="aca-count-box__num">{spaces.length}</span>
          <span className="aca-count-box__label">Pending Review</span>
        </div>
      </div>

      {/* CONTENT */}
      {spaces.length === 0 ? (
        <Empty description="No pending requests" className="aca-empty" />
      ) : (
        <div className="aca-grid">
          {spaces.map((space) => (
            <div
              key={space._id}
              className="aca-card"
              onClick={() => navigate(`/commercial-space/${space.slug}`)}
            >
              {/* Image */}
              <div className="aca-card__img-wrap">
                <img
                  alt={space.title}
                  src={space.thumbnail || "https://placehold.co/400x250?text=No+Image"}
                  className="aca-card__img"
                />
                <button
                  className="aca-card__view-btn"
                  aria-label="View listing"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/commercial-space/${space.slug}`);
                  }}
                >
                  <EyeOutlined />
                </button>
                <div className="aca-card__badge">
                  <span className="aca-card__badge-dot" />
                  Pending
                </div>
              </div>

              {/* Body */}
              <div className="aca-card__body">
                <h5 className="aca-card__title" title={space.title}>
                  {space.title}
                </h5>

                <div className="aca-card__price">
                  <span className="aca-card__price-currency">₹</span>
                  <span className="aca-card__price-amount">
                    {space.pricing?.price?.toLocaleString() || 0}
                  </span>
                  <span className="aca-card__price-period">
                    /{space.pricing?.type || "listing"}
                  </span>
                </div>

                <div className="aca-card__meta">
                  <span>{space.location?.city}, {space.location?.state}</span>
                  <span className="aca-card__meta-dot">•</span>
                  <span>{space.propertyDetails?.areaSqFt} sqft</span>
                  <span className="aca-card__meta-dot">•</span>
                  <span>{space.propertyDetails?.propertyType}</span>
                </div>

                <div className="aca-card__actions">
                  <Button
                    type="primary"
                    className="aca-btn aca-btn--approve"
                    icon={<CheckCircleOutlined />}
                    onClick={(e) => { e.stopPropagation(); handleApprove(space._id); }}
                  >
                    Approve
                  </Button>
                  <Button
                    danger
                    className="aca-btn aca-btn--reject"
                    icon={<CloseCircleOutlined />}
                    onClick={(e) => { e.stopPropagation(); openRejectModal(space._id); }}
                  >
                    Reject
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* PAGINATION */}
      {spaces.length > 0 && (
        <div className="aca-pagination">
          <Pagination
            current={currentPage}
            total={total}
            pageSize={15}
            onChange={(page) => setCurrentPage(page)}
            showSizeChanger={false}
          />
        </div>
      )}

      {/* REJECT MODAL */}
      <Modal
        title="Reject Listing"
        open={isModalOpen}
        okText="Confirm Rejection"
        cancelText="Cancel"
        okButtonProps={{ disabled: !reason.trim(), danger: true }}
        onOk={handleConfirmReject}
        onCancel={closeModal}
        className="aca-modal"
      >
        <div className="aca-modal__body">
          <Text strong className="aca-modal__label">Rejection Reason</Text>
          <Input.TextArea
            rows={4}
            placeholder="Please provide a detailed reason for rejection..."
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            className="aca-modal__textarea"
          />
          {!reason.trim() && (
            <p className="aca-modal__error">Reason is required</p>
          )}
        </div>
      </Modal>
    </div>
  );
}

export default AdminCommercialApprovals;